import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import csiCard from "csi/card/_base";
import CardItemCard from "designSystem/components/card/CardItemCard";
import { DataGridCardProps, Grid, GridColumn, PopperInContainer, SearchSingleSelect, TextField } from "designSystem/index";
import { ILocaleProvider, useLocale } from "designSystem/locale";
import NodeDataStore from "csi/datastore/NodeDataStore";
import { C8Node, IDataStore } from "csi/typings";
import Search from "csi/xml/Search";
import React, { FunctionComponent, useEffect, useState } from "react";

export interface CardDataGridSingleSelectProps<T> {
	/** card component, would override the default one if configured */
	CardComponent?: FunctionComponent<DataGridCardProps<T>>;

	/** indicate whether or not disable the component so that user can't be able to open the dropdown
	 * Note: in contrast, "getDisableItem" is to make the item in the dropdown disabled
	*/
	disabled?: boolean;

	/** callback to determine whether or not should disable this item so that the user can't be able to select that item
	 * Note: in contrast, "disabled" is to make textbox disabled 
	*/
	getDisableItem?: (item: T) => boolean;

	/** function to render the text in the textbox */
	getDisplayText: (id: string, loc: ILocaleProvider) => string;

	/** the type of the node */
	nodeType: string;

	/** callback called when an item is selected or when blurred/escaped with no value */
	onSelectCard: (value?: string) => void;

	/** the store for the cards */
	store: IDataStore<T>;

	/** the selected value */
	value?: string;
}

const CardDataGridSingleSelect = <T extends C8Node>(props: CardDataGridSingleSelectProps<T>) => {
	const [anchorEl, setAnchorEl] = useState(null);
	const {
		CardComponent,
		disabled,
		getDisableItem,
		getDisplayText,
		nodeType,
		onSelectCard,
		store,
		value,
	} = props;

	const loc = useLocale();

	/** search callback for ComponentItemDataStore */
	const onSearchCardDataStore = async (store: NodeDataStore, searchText: string): Promise<NodeDataStore> => {
		const { cache, search } = store;
		const originalQuery = search.getQuery();
		const originalParams = search.getParameters();
		const cardSpecs = csiCard.getAttributeCardSlimSpecs(nodeType);

		if (searchText) {
			const newSearch = new Search({ ...originalParams });
			const q = newSearch.getQuery();
			csiCard.getQueryForSpecs(cardSpecs, searchText, q);
			q.importQuery(originalQuery);

			return new NodeDataStore(newSearch, cache);
		} else {
			return new NodeDataStore(search, cache);
		}
	};

	const [id, setId] = useState<string>(value || "");
	const [text, setText] = useState<string>(getDisplayText(id, loc) || "");

	/** close the popup and proceed */
	const _onClickTextBox = (event) => {
		if (disabled) {
			return;
		}

		setAnchorEl(anchorEl ? null : event.currentTarget);
	};

	/** update the value of textbox to keep the value in sync */
	const _onSelectItem = (value?: string) => {
		anchorEl?.focus();
		setAnchorEl(null);

		// only proceed if value changes
		if (id !== value) {
			if (value) {
				setId(value);
				setText(getDisplayText(value, loc));
			}

			onSelectCard?.(value);
		}
	};

	useEffect(() => {
		setText(getDisplayText(value, loc));
		setId(value);
	}, [value]);

	return (
		<GridColumn>
			<Grid item>
				<TextField
					InputProps={{
						endAdornment: <ArrowDropDownIcon />,
					}}
					disabled={disabled}
					fullWidth
					onClick={_onClickTextBox}
					placeholderRes="SelectSomething"
					placeholderResParams={[nodeType]}
					style={{
						width: 200,
					}}
					value={text}
				/>

			</Grid>
			<Grid item>
				<PopperInContainer anchorEl={anchorEl}>
					<SearchSingleSelect<T>
						dataGridProps={{
							CardComponent: CardComponent || CardItemCard,
							store,
						}}
						getDisableItem={getDisableItem}
						inPopup
						listItemProps={{
							height: 56,
						}}
						maxHeight={300}
						onSearchStore={onSearchCardDataStore}
						onSelect={_onSelectItem}
						style={{
							width: 200,
						}}
						value={id}
					/>
				</PopperInContainer>
			</Grid>
		</GridColumn>
	);
};

export default CardDataGridSingleSelect;